"use client"

import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ref, onValue, update } from "../lib/firebase-database"
import { auth, db } from "../firebase"
import { useAuth } from "../hooks/useAuth"
import {
  getStorage,
  ref as storageRef,
  listAll,
  getDownloadURL,
} from "../lib/firebase-storage"
import { AdminLayout } from "./AdminLayout"
import AnimatedVillaImage from "./AnimatedVillaImage"
import { Loader2, Search, X, Check } from "lucide-react"

export default function AdminVillaImageManager() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [villas, setVillas] = useState([])
  const [selectedVilla, setSelectedVilla] = useState(null)
  const [newImages, setNewImages] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [storageImages, setStorageImages] = useState([])
  const [isLoadingImages, setIsLoadingImages] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  useEffect(() => {
    if (!user || !isAdmin) navigate("/")
  }, [user, isAdmin, navigate])

  useEffect(() => {
    const villasRef = ref(db, "villas")
    const unsubscribe = onValue(villasRef, (snapshot) => {
      const data = snapshot.val()
      if (data) {
        setVillas(
          Object.entries(data).map(([id, value]) => ({ id, ...value }))
        )
      } else {
        setVillas([])
      }
    })
    return () => {
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [])

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files)
    const fileURLs = files.map((file) => URL.createObjectURL(file))
    setNewImages(fileURLs)
  }

  const handleSaveImages = async () => {
    if (!selectedVilla || newImages.length === 0) return
    setIsUploading(true)
    await update(ref(db, `villas/${selectedVilla.id}`), { images: newImages })
    setIsUploading(false)
    setSelectedVilla(null)
    setNewImages([])
  }

  const fetchVillaStorageImages = async (villaId) => {
    setIsLoadingImages(true)
    setStorageImages([])
    try {
      const storage = getStorage()
      const folderRef = storageRef(storage, `villas/${villaId}/`)
      const res = await listAll(folderRef)
      const urls = await Promise.all(
        res.items.map((item) => getDownloadURL(item))
      )
      setStorageImages(urls)
    } catch (_) {
      setStorageImages([])
    }
    setIsLoadingImages(false)
  }

  const handleLogout = () => {
    auth.signOut().then(() => navigate("/login"))
  }

  const filteredVillas = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return villas
    return villas.filter(
      (v) =>
        v.name?.toLowerCase().includes(q) ||
        v.mainCity?.toLowerCase().includes(q)
    )
  }, [villas, searchQuery])

  const closePreview = () => {
    setSelectedVilla(null)
    setNewImages([])
    setStorageImages([])
  }

  return (
    <AdminLayout user={user} onLogout={handleLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Villa Image Manager
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pick a villa to update or replace its gallery images.
          </p>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search villas"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVillas.map((villa) => (
            <button
              key={villa.id}
              type="button"
              onClick={() => {
                setSelectedVilla(villa)
                fetchVillaStorageImages(villa.id)
                setNewImages([])
              }}
              className="text-left bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-black"
            >
              <div className="w-full h-40 bg-gray-100">
                <img
                  src={villa.thumbnail || villa.images?.[0] || "/placeholder.svg"}
                  alt={villa.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-3">
                <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                  {villa.name}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                  {villa.mainCity || villa.address || "—"}
                </div>
              </div>
            </button>
          ))}
        </div>

        {filteredVillas.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
            No villas to show.
          </div>
        )}
      </div>

      {selectedVilla && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div>
                <h2 className="text-base font-semibold text-gray-900 line-clamp-1">
                  {selectedVilla.name}
                </h2>
                <p className="text-xs text-gray-500">Manage gallery</p>
              </div>
              <button
                type="button"
                onClick={closePreview}
                className="p-1.5 text-gray-700 hover:bg-gray-100 rounded-md"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              <div className="rounded-lg overflow-hidden border border-gray-200">
                <AnimatedVillaImage
                  src={
                    newImages[0] ||
                    selectedVilla.images?.[0] ||
                    "/placeholder.svg"
                  }
                  alt={selectedVilla.name}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add or replace images
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-black file:text-white hover:file:bg-gray-900"
                />
                {newImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {newImages.map((img, idx) => (
                      <img
                        key={idx}
                        src={img}
                        alt={`New ${idx + 1}`}
                        className="w-full h-20 object-cover rounded-md border border-gray-200"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Storage images
                </label>
                {isLoadingImages ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading
                  </div>
                ) : storageImages.length === 0 ? (
                  <div className="text-sm text-gray-500 py-4">
                    No images in storage.
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {storageImages.map((img, idx) => {
                      const selected = newImages.includes(img)
                      return (
                        <button
                          key={idx}
                          type="button"
                          onClick={() =>
                            setNewImages((prev) =>
                              prev.includes(img)
                                ? prev.filter((i) => i !== img)
                                : [...prev, img]
                            )
                          }
                          className={`relative w-full h-20 rounded-md overflow-hidden border ${
                            selected ? "border-black" : "border-gray-200"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`Villa ${idx + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {selected && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                              <Check className="h-5 w-5 text-white" />
                            </div>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={closePreview}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveImages}
                disabled={isUploading || newImages.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-black rounded-md hover:bg-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isUploading && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {isUploading ? "Saving..." : "Save Images"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
